export interface UnitLabels {
  one: string;
  few: string;
  many: string;
  /** Genitive singular, used after a decimal: "2,5 blistra". */
  fraction: string;
}

const MILLI = 1000;

export function splitPackagesAndUnits(
  quantityMilli: number,
  unitsPerPackage: number,
): { packages: number; unitsMilli: number } {
  const packageMilli = unitsPerPackage * MILLI;
  const absolute = Math.abs(quantityMilli);
  const packages = Math.floor(absolute / packageMilli);
  const unitsMilli = absolute - packages * packageMilli;
  if (quantityMilli >= 0) {
    return { packages, unitsMilli };
  }
  return packages > 0
    ? { packages: -packages, unitsMilli }
    : { packages: 0, unitsMilli: -unitsMilli };
}

export function formatMilli(milli: number): string {
  const sign = milli < 0 ? '-' : '';
  const absolute = Math.abs(milli);
  const whole = Math.floor(absolute / MILLI);
  const fraction = absolute % MILLI;
  if (fraction === 0) {
    return `${sign}${whole}`;
  }
  return `${sign}${whole},${String(fraction)
    .padStart(3, '0')
    .replace(/0+$/, '')}`;
}

export function pluralForm(milli: number): keyof UnitLabels {
  const absolute = Math.abs(milli);
  if (absolute % MILLI !== 0) {
    return 'fraction';
  }
  const count = absolute / MILLI;
  if (count === 1) {
    return 'one';
  }
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  if (
    lastDigit >= 2 &&
    lastDigit <= 4 &&
    (lastTwoDigits < 12 || lastTwoDigits > 14)
  ) {
    return 'few';
  }
  return 'many';
}

export function formatUnits(milli: number, labels: UnitLabels): string {
  return `${formatMilli(milli)} ${labels[pluralForm(milli)]}`;
}

/** "3 op. + 2 blistry"; negative stock reads "-(3 op. + 2 blistry)". */
export function formatStock(
  quantityMilli: number,
  unitsPerPackage: number,
  labels: UnitLabels,
  packageLabel: string,
): string {
  if (unitsPerPackage <= 1) {
    return formatUnits(quantityMilli, labels);
  }
  const { packages, unitsMilli } = splitPackagesAndUnits(
    Math.abs(quantityMilli),
    unitsPerPackage,
  );
  const parts = [
    ...(packages > 0 ? [`${packages} ${packageLabel}`] : []),
    ...(unitsMilli > 0 || packages === 0
      ? [formatUnits(unitsMilli, labels)]
      : []),
  ];
  const text = parts.join(' + ');
  if (quantityMilli >= 0) {
    return text;
  }
  return parts.length > 1 ? `-(${text})` : `-${text}`;
}

export function parseQuantityInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,3})?$/.test(normalized)) {
    return null;
  }
  const parsed = Number(normalized);
  return parsed > 0 ? parsed : null;
}

/**
 * A stored quantity (a `WZ` line's `inputQuantity`) as integer thousandths,
 * rounded like the backend `lineValueMinor`, so value and display agree.
 */
export function toMilli(quantity: number): number {
  return Math.round(quantity * MILLI);
}

/**
 * A typed quantity (comma or dot, at most 3 decimals) as integer thousandths,
 * read from the digits, so money previews never multiply a float (#668).
 */
export function parseQuantityMilli(value: string): number | null {
  const match = /^(\d+)(?:\.(\d{1,3}))?$/.exec(value.trim().replace(',', '.'));
  if (!match) {
    return null;
  }
  const milli =
    Number(match[1]) * MILLI + Number((match[2] ?? '').padEnd(3, '0'));
  return Number.isSafeInteger(milli) && milli > 0 ? milli : null;
}
